import React, { useMemo } from 'react';
import { CheckCircle2, Circle, Sparkles } from 'lucide-react';

// Weights are tuned so a brand-new profile starts low (~0%) and a "great"
// profile sits around 90-100%. Avatar + bio + ≥3 interests + ≥2 gallery
// photos is the bar for "looks complete".
const CRITERIA = [
  { key: 'photo', label: 'Add a profile photo', weight: 25,
    check: (p) => !!p.photo },
  { key: 'gallery', label: 'Add at least 2 gallery photos', weight: 20,
    check: (p) => (p.gallery?.length || 0) >= 2 },
  { key: 'bio', label: 'Write a bio (40+ characters)', weight: 15,
    check: (p) => (p.bio || '').trim().length >= 40 },
  { key: 'preferences', label: 'Pick 8 — Interest-Into', weight: 25,
    check: (p) => (p.preferences?.length || 0) >= 8 },
  { key: 'location', label: 'Set your location', weight: 15,
    check: (p) => (p.location || '').trim().length > 0 },
];

const ProfileCompletionScore = ({ photo, gallery, bio, interests, location, preferences }) => {
  const { score, missing } = useMemo(() => {
    const p = { photo, gallery, bio, interests, location, preferences };
    let s = 0;
    const missed = [];
    for (const c of CRITERIA) {
      if (c.check(p)) s += c.weight;
      else missed.push(c);
    }
    return { score: s, missing: missed };
  }, [photo, gallery, bio, interests, location, preferences]);

  // Don't render the card once they're "done" — keep /me uncluttered.
  if (score >= 100) return null;

  let band;
  if (score >= 80) band = { color: 'text-emerald-300', barFrom: 'from-emerald-500', barTo: 'to-emerald-300', label: 'Almost there!' };
  else if (score >= 50) band = { color: 'text-amber-300', barFrom: 'from-amber-500', barTo: 'to-fnl-pink', label: 'Getting there' };
  else band = { color: 'text-fnl-pink', barFrom: 'from-fnl-pink', barTo: 'to-pink-300', label: 'Just getting started' };

  // Show top 2 missing items (highest weight first) as a clear next-action list.
  const top = [...missing].sort((a, b) => b.weight - a.weight).slice(0, 2);

  return (
    <div
      className="bg-fnl-purple/60 border border-purple-900/40 rounded-2xl p-5 mb-6"
      data-testid="profile-completion-card"
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-fnl-pink text-xs uppercase tracking-wider font-bold mb-1">
            <Sparkles className="w-3.5 h-3.5" />Profile strength
          </div>
          <div className={`text-2xl font-extrabold ${band.color}`}>
            <span data-testid="profile-completion-score">{score}%</span>
            <span className="text-white/85 text-sm font-semibold ml-2">— {band.label}</span>
          </div>
        </div>
        <div className="text-xs text-purple-300 max-w-xs">
          Profiles with photos + a bio get up to <b className="text-fnl-pink">5×</b> the likes.
        </div>
      </div>
      <div className="h-2 w-full rounded-full bg-fnl-purple-light/40 overflow-hidden mb-4">
        <div
          className={`h-full bg-gradient-to-r ${band.barFrom} ${band.barTo} transition-all duration-500`}
          style={{ width: `${score}%` }}
          data-testid="profile-completion-bar"
        />
      </div>
      <ul className="space-y-1.5 text-sm" data-testid="profile-completion-todo">
        {CRITERIA.map((c) => {
          const done = c.check({ photo, gallery, bio, interests, location, preferences });
          const isTop = top.includes(c);
          return (
            <li
              key={c.key}
              className={`flex items-center gap-2 ${
                done ? 'text-purple-400 line-through' :
                isTop ? 'text-white font-semibold' : 'text-purple-200'
              }`}
              data-testid={`completion-${c.key}-${done ? 'done' : 'todo'}`}
            >
              {done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                : <Circle className={`w-4 h-4 shrink-0 ${isTop ? 'text-fnl-pink' : 'text-purple-500'}`} />}
              <span>{c.label}</span>
              {!done && isTop && (
                <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-fnl-pink">
                  +{c.weight}%
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ProfileCompletionScore;
